import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { applySeoCustom } from "../lib/seo";
import { MaskedLines, Reveal, SectionHeading } from "../components/site/Reveal";
import { CTABand } from "../components/site/CTABand";

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  ["brief", "The brief"],
  ["design_objectives", "Design objectives"],
  ["spec_rationale", "Why this specification"],
  ["technical", "Technical & installation"],
  ["challenges", "Challenges"],
  ["outcome", "The outcome"],
];

export default function ProjectJournal() {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [error, setError] = useState(false);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);

  useEffect(() => {
    setCs(null); setError(false);
    fetch(`${API}/api/case-studies/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setCs)
      .catch(() => setError(true));
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (cs) applySeoCustom({
      title: `${cs.title} | Cedar Views Projects`,
      description: cs.brief || cs.body || `${cs.title} — a Cedar Views installation in ${cs.location}.`,
      path: `/projects/${cs.slug}/`,
    });
  }, [cs]);

  if (error) return <Navigate to="/projects/" replace />;
  if (!cs) return <main className="min-h-[60vh]" data-testid="project-loading" />;

  const meta = [
    ["Location", cs.location], ["Property", cs.property_type],
    ["Manufacturer", cs.manufacturer], ["Product / system", cs.product || cs.spec],
  ].filter(([, v]) => v);
  const sections = SECTIONS.filter(([k]) => cs[k]);
  let n = 0;

  return (
    <main data-testid="project-journal-page">
      <section className="relative h-[72vh] min-h-[520px] overflow-hidden flex items-end">
        <motion.div style={{ y }} className="absolute inset-0">
          {cs.image && <img src={cs.image} alt={cs.title} className="w-full h-[120%] object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050A15] via-[#050A15]/50 to-[#050A15]/25" />
        </motion.div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pb-16 w-full">
          <Reveal delay={0.1}>
            <p className="overline-x mb-5">
              <Link to="/projects/" className="hover:text-white transition-colors">Project journal</Link>
              {cs.location && <><span className="mx-2">/</span>{cs.location}</>}
            </p>
          </Reveal>
          <MaskedLines as="h1" lines={[cs.title]} delay={0.2}
            className="font-display tracking-tighter text-3xl sm:text-4xl lg:text-6xl text-white leading-[1.02] max-w-5xl" />
          {cs.spec && <Reveal delay={0.5} className="mt-5"><p className="font-mono-x text-sm text-white/60">{cs.spec}</p></Reveal>}
        </div>
      </section>

      {meta.length > 0 && (
        <section className="border-y hairline bg-[#070E1D]" data-testid="project-meta">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
            {meta.map(([k, v]) => (
              <div key={k}>
                <p className="font-mono-x text-[10px] tracking-[0.25em] uppercase text-white/40">{k}</p>
                <p className="text-white mt-2 text-sm leading-relaxed">{v}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {cs.body && (
        <section className="max-w-[900px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28">
          <Reveal><p className="text-white/70 leading-relaxed text-lg">{cs.body}</p></Reveal>
        </section>
      )}

      {sections.map(([k, label]) => {
        n += 1;
        return (
          <section key={k} className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14 lg:py-16" data-testid={`project-section-${k}`}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <span className="font-mono-x text-xs text-[#3C64A8]">{String(n).padStart(2, "0")}</span>
                <h2 className="font-display font-medium text-2xl text-white mt-3 tracking-tight">{label}</h2>
              </div>
              <div className="lg:col-span-8">
                {cs[k].split("\n").filter(Boolean).map((p, i) => (
                  <p key={i} className="text-white/60 leading-relaxed mb-4">{p}</p>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {(cs.gallery || []).length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20" data-testid="project-gallery">
          <SectionHeading number={String(n + 1).padStart(2, "0")} kicker="In detail" title="Project gallery." />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {cs.gallery.map((src, i) => (
              <Reveal key={src} delay={(i % 3) * 0.06} className={i % 5 === 0 ? "col-span-2 row-span-2" : ""}>
                <div className="img-hover h-full">
                  <img src={src} alt={`${cs.title} — detail ${i + 1}`} loading="lazy" className="w-full h-full object-cover aspect-square" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {cs.video && (
        <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16" data-testid="project-video">
          <div className="aspect-video border border-white/10">
            <iframe src={cs.video} title={cs.title} className="w-full h-full" allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
          </div>
        </section>
      )}

      {((cs.related_products || []).length > 0 || cs.related_area || (cs.related_articles || []).length > 0) && (
        <section className="border-t hairline bg-[#070E1D]" data-testid="project-related">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
            <Reveal>
              <p className="overline-x mb-6">Explore further</p>
              <div className="flex flex-wrap gap-3">
                {(cs.related_products || []).filter((r) => r.label && r.to).map((r) => (
                  <Link key={r.to} to={r.to} className="px-5 py-2.5 border border-white/20 text-white/80 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-white hover:text-[#0B162C] transition-colors">
                    {r.label}
                  </Link>
                ))}
                {(cs.related_articles || []).filter((r) => r.label && r.to).map((r) => (
                  <Link key={r.to} to={r.to} className="px-5 py-2.5 border border-white/20 text-white/80 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-white hover:text-[#0B162C] transition-colors">
                    {r.label}
                  </Link>
                ))}
                {cs.related_area && (
                  <Link to={`/areas/${cs.related_area}/`} className="px-5 py-2.5 border border-white/20 text-white/80 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-white hover:text-[#0B162C] transition-colors inline-flex items-center gap-2">
                    More work in this area <ArrowRight size={12} />
                  </Link>
                )}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <CTABand product={cs.product || "Windows & Doors"} />
    </main>
  );
}
